import { motion, useReducedMotion } from 'motion/react'
import { cn } from '@/lib/utils'
import './app-mark.css'

const M = 'M69 527V140a72 72 0 0 1 130.9-41.4L365.5 334 531.1 98.6A72 72 0 0 1 662 140v125'
const TAIL = 'M549 690V600c0-62 40-104 95-116'
const STAR = 'M787 38c5 34 18 47 52 52-34 5-47 18-52 52-5-34-18-47-52-52 34-5 47-18 52-52z'
const ease = [0.65, 0, 0.35, 1] as const
/** The star's box in the mark's viewBox (-1 -64 880 880), as fractions, for the looping overlay. */
const STAR_BOX = { left: `${(736 / 880) * 100}%`, top: `${(102 / 880) * 100}%`, width: `${(104 / 880) * 100}%`, height: `${(104 / 880) * 100}%` }

type Props = {
  className?: string
  /** Pop in on mount (default). */
  animate?: boolean
  /** Draw the letters stroke by stroke, then light the star (the login's entrance). */
  draw?: boolean
  /** With `draw`: keep the star twinkling (login) or light it once (sidebar). */
  loop?: boolean
}

/**
 * Mira's mark: a monoline M whose last stroke carries the Arabic meem (م), with a star.
 * Drawn inline so the letters take the current colour (primary by default) in light and dark.
 */
export function AppMark({ className, animate = true, draw = false, loop = true }: Props) {
  const reduce = useReducedMotion()
  const drawing = draw && !reduce
  // The endless twinkle runs as a CSS animation on its own layer (app-mark.css), so it costs no
  // main-thread work per frame; a JS-driven loop on an SVG path re-laid out the page every frame.
  const twinkle = drawing && loop
  const stroke = (delay: number, duration: number) =>
    drawing ? { initial: { pathLength: 0 }, animate: { pathLength: 1 }, transition: { delay, duration, ease } } : {}

  const svg = (
    <svg viewBox="-1 -64 880 880" aria-hidden className="size-full overflow-visible">
      <g fill="none" stroke="currentColor" strokeWidth={68} strokeLinecap="round" strokeLinejoin="round">
        <motion.path d={M} {...stroke(0.15, 1.1)} />
        <motion.circle cx={720} cy={440} r={88} {...stroke(1, 0.6)} />
        <motion.path d={TAIL} {...stroke(1.35, 0.4)} />
      </g>
      {!twinkle && (
        <motion.path
          className="fill-mark-star"
          style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
          d={STAR}
          {...(drawing && {
            initial: { scale: 0, rotate: -90, opacity: 0 },
            animate: { scale: [0, 1.5, 1, 1.25, 1], rotate: [-90, 0, 0, 45, 0], opacity: 1 },
            transition: { delay: 1.4, duration: 0.9, times: [0, 0.45, 0.7, 0.85, 1] },
          })}
        />
      )}
    </svg>
  )

  if (twinkle)
    return (
      <span className={cn('relative grid shrink-0 place-items-center text-primary', className)}>
        {svg}
        <span className="mark-star-loop" style={STAR_BOX}>
          <svg viewBox="735 38 104 104" aria-hidden className="size-full overflow-visible"><path className="fill-mark-star" d={STAR} /></svg>
        </span>
      </span>
    )
  if (!animate || drawing) return <span className={cn('grid shrink-0 place-items-center text-primary', className)}>{svg}</span>
  return (
    <motion.span
      className={cn('grid shrink-0 place-items-center text-primary', className)}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: 'spring', stiffness: 180, damping: 18 }}
    >
      {svg}
    </motion.span>
  )
}
